import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { environment } from '@env/environment';

/**
 * Dev-only mock for the feature-flag endpoint (`GET …/features/me`).
 *
 * Fase A runs with `environment.useMocks = true` and no BFF, so the chassis
 * `FeatureService` (which fetches `/features/me`) would otherwise fall back to
 * an empty map and the `featureGuard` would block `mod.cms.manage` routes.
 * This interceptor short-circuits that single request with a map that enables
 * the CMS module so the admin content editor is reachable end-to-end against
 * the in-memory SDK. It mirrors the SDK mock pattern (no chassis service edits)
 * and is a strict no-op when `useMocks` is false (real BFF answers).
 *
 * Scope is intentionally narrow: it ONLY matches the features endpoint and only
 * a GET, so every other request flows through untouched.
 */
const FEATURES_PATH = '/features/me';

/**
 * Endpoint público de Foundation que resuelve los flags `mod.cms.storefront.*`
 * per-org (`GET …/cms/delivery/features`, PR backend #250). En dev el
 * `StorefrontFeatureService` lo pega igual que en prod (mismo camino runtime);
 * este mock lo short-circuitea con el default de entorno, así el piloto de
 * ofertas se ve en dev/preview sin BFF. Matchea el sufijo del proxy same-origin
 * (`/delivery-proxy/features`) o la base directa (`…/cms/delivery/features`).
 */
const STOREFRONT_FEATURES_SUFFIXES = ['/delivery-proxy/features', '/cms/delivery/features'] as const;

/** Feature map served in dev: CMS module + management surface enabled. */
const MOCK_FEATURES = {
  plan: 'enterprise',
  features: {
    'mod.cms': true,
    'mod.cms.manage': true,
    'mod.cms.delivery': true,
  },
} as const;

/**
 * Envelope público de features del storefront servido en dev.
 *
 * Reproduce el contrato del backend DESPUÉS de #252 (conjunción de ancestros ya
 * resuelta en el server, en prod desde 2026-07-23):
 *  - publica la clave RAÍZ `mod.cms.storefront` además de sus hijos — que es lo
 *    que el allow-list de prod todavía NO hace (ver `storefront-feature.ts`);
 *  - aplica la conjunción acá: con la raíz en OFF, `offers` sale en OFF **sin
 *    importar** `environment.featuredOffersEnabled`. Si el mock sirviera el hijo
 *    en ON con el padre en OFF, dev mostraría un estado que prod ya no puede
 *    producir, y la verificación local no probaría nada.
 *
 * Los valores salen de `environment.*`, así que apagar la tienda en dev para
 * verificar la ocultación es cambiar `storefrontEnabled: false` y recargar.
 */
const MOCK_STOREFRONT_FEATURES = {
  success: true,
  data: {
    features: {
      'mod.cms.storefront': environment.storefrontEnabled,
      // Conjunción: cualquier ancestro OFF apaga al hijo.
      'mod.cms.storefront.offers':
        environment.storefrontEnabled && environment.featuredOffersEnabled,
    },
  },
} as const;

/**
 * Intercepts the feature-flag fetches in mock mode and returns an enabled CMS
 * feature map (chassis `/features/me`) and the storefront-public flags
 * (`…/features`); passes every other request (and all requests in real mode)
 * straight through.
 * @param req Outgoing HTTP request.
 * @param next Next handler in the interceptor chain.
 * @returns The mocked features response in dev, otherwise the live stream.
 */
export const cmsMockFeaturesInterceptor: HttpInterceptorFn = (req, next) => {
  if (!environment.useMocks || req.method !== 'GET') {
    return next(req);
  }
  const url = req.url.split('?')[0];
  if (url.includes(FEATURES_PATH)) {
    return of(new HttpResponse({ status: 200, body: MOCK_FEATURES }));
  }
  if (STOREFRONT_FEATURES_SUFFIXES.some((suffix) => url.endsWith(suffix))) {
    return of(new HttpResponse({ status: 200, body: MOCK_STOREFRONT_FEATURES }));
  }
  return next(req);
};
